import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { isEmpty } from "@swiggy-private/core-ui";
import { isIxigoFlow } from "@swiggy-private/checkout-core";
import {
    DeliveryDetailsWidget,
    TEXTS,
    IHeaderData,
    IDeliveryRowData,
    IToolTipData,
    IIrctcDeliveryDetailsData,
} from "@swiggy-private/cart-ui";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as PhoneFilledIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/phoneFilled/16";
import { default as SeatIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/seat/16";
import { IconName } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb/types";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import { IrctcAddressChangeFormKeys, UserAddress } from "@swiggy-private/cart-types";

import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { getIrctcIdentifier } from "@cart-client/helpers/Cart";
import {
    getIrctcDeliveryDetailsData,
    getIrctcPnrToolTipText,
    getNonPnrIrctcIdentifier,
} from "@cart-client/helpers/Irctc";
import { PnrDataContext } from "@cart-client/contexts/PnrDataContext";
import { useIrctcAnalytics } from "@cart-client/hooks/useIrctcAnalytics";
import { IRCTC_TEST_IDS } from "@cart-client/constants/Irctc";

const IRCTC_ICON_BACKGROUND = "#004CC0";

interface UseIrctcCartParams {
    cartResponse: ReviewedCart | null;
    selectedAddress: UserAddress | null;
    showIrctcCart: boolean;
    setIrctcCartSession: (value: boolean) => void;
    handleIrctcEditAddress: (ctaType: IrctcAddressChangeFormKeys) => () => void;
}

interface UseIrctcCartReturn {
    renderIrctcAddressChange: React.ReactNode;
}

export const useIrctcCart = ({
    cartResponse,
    selectedAddress,
    showIrctcCart,
    setIrctcCartSession,
    handleIrctcEditAddress,
}: UseIrctcCartParams): UseIrctcCartReturn => {
    const { setIsIrctcPnrHalfCardOpen } = useContext(PnrDataContext);
    const [tooltipDismissed, setTooltipDismissed] = useState(false);

    const { trackDeliveryBannerImpression, trackUserDetailsBannerImpression, trackEnterPnrImpression } =
        useIrctcAnalytics(cartResponse, selectedAddress);

    const nonPnrData = useMemo(() => getNonPnrIrctcIdentifier(cartResponse), [cartResponse]);

    useEffect(() => {
        if (nonPnrData && isEmpty(cartResponse?.data?.irctc_data?.pnr_number)) {
            trackEnterPnrImpression();
        }
    }, [nonPnrData, cartResponse?.data?.irctc_data?.pnr_number, trackEnterPnrImpression]);

    const irctcSeatTrainKey = useMemo(() => {
        const irctcData = getIrctcIdentifier(cartResponse);
        return [irctcData?.coach, irctcData?.berth, irctcData?.train_number, irctcData?.train_name].join("|");
    }, [cartResponse]);

    const lastIrctcSeatTrainKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (!showIrctcCart) {
            lastIrctcSeatTrainKeyRef.current = null;
            return;
        }
        if (lastIrctcSeatTrainKeyRef.current === irctcSeatTrainKey) {
            return;
        }
        lastIrctcSeatTrainKeyRef.current = irctcSeatTrainKey;
        trackUserDetailsBannerImpression();
    }, [showIrctcCart, irctcSeatTrainKey, trackUserDetailsBannerImpression]);
    const handleEnterPnr = useCallback(() => {
        setIsIrctcPnrHalfCardOpen(true);
    }, [setIsIrctcPnrHalfCardOpen]);

    const handleIrctcActionClick = useCallback(() => {
        const isPnrEntered = !isEmpty(cartResponse?.data?.irctc_data?.pnr_number);
        if (nonPnrData && !isPnrEntered) {
            return handleEnterPnr();
        }
        return handleIrctcEditAddress(IrctcAddressChangeFormKeys.SEAT_NO)();
    }, [nonPnrData, cartResponse, handleEnterPnr, handleIrctcEditAddress]);

    const renderIrctcAddressChange = useMemo(() => {
        const irctcData = getIrctcIdentifier(cartResponse);
        const onPremiseData = getIrctcDeliveryDetailsData(cartResponse, selectedAddress);
        const pnrToolTipText = getIrctcPnrToolTipText(cartResponse);
        if (showIrctcCart && irctcData) {
            // to use this flag to redirect to irctc homepage when cart is clear on Browse Restaurant CTA.
            setIrctcCartSession(true);
            const data = onPremiseData as IIrctcDeliveryDetailsData;

            const headerData: IHeaderData = {
                title: data.title,
                subtitle: data.subtitle,
                imageUrl: data.widgetImage,
                background: data.background,
            };

            const deliveryContentData: IDeliveryRowData = {
                icon: <GenericIcon Icon={SeatIcon} fillColor="background_Primary" />,
                iconBackground: IRCTC_ICON_BACKGROUND,
                title: data.orderTypeTitle || TEXTS.DELIVERY_DETAILS,
                subtitle: data.orderTypeSubtitle,
                ctaLabel: nonPnrData ? TEXTS.VERIFY_PNR_CTA : TEXTS.DEFAULT_EDIT_CTA,
                ctaAction: handleIrctcActionClick,
                ctaTestId: IRCTC_TEST_IDS.DELIVERY_DETAILS_CTA,
            };

            const deliveryContactData: IDeliveryRowData = {
                icon: (
                    <GenericIcon
                        Icon={PhoneFilledIcon}
                        strokeColor="background_Primary"
                        fillColor="background_Primary"
                    />
                ),
                iconBackground: IRCTC_ICON_BACKGROUND,
                title: TEXTS.TRAVELLER_MOBILE_NO,
                subtitle: data.mobileNumber,
                ctaLabel: TEXTS.DEFAULT_EDIT_CTA,
                ctaAction: handleIrctcEditAddress(IrctcAddressChangeFormKeys.CONTACT_NO),
                ctaTestId: IRCTC_TEST_IDS.DELIVERY_CONTACT_CTA,
            };

            const toolTipData: IToolTipData = {
                text: (tooltipDismissed ? null : pnrToolTipText) ?? "",
                onDismiss: () => setTooltipDismissed(true),
                leftAsset: {
                    name: TEXTS.ON_PREMISE_TOOLTIP_LEFT_ASSET_NAME as IconName,
                    fillColor: TEXTS.ON_PREMISE_TOOLTIP_LEFT_ASSET_FILL_COLOR as keyof IColors,
                    strokeColor: TEXTS.ON_PREMISE_TOOLTIP_LEFT_ASSET_STROKE_COLOR as keyof IColors,
                },
            };

            return (
                <DeliveryDetailsWidget
                    headerData={headerData}
                    deliveryContentData={deliveryContentData}
                    deliveryContactData={deliveryContactData}
                    toolTipData={toolTipData}
                    hideEditCta={isIxigoFlow()}
                    onLoadImpression={trackDeliveryBannerImpression}
                />
            );
        }
        return null;
    }, [
        cartResponse,
        setIrctcCartSession,
        showIrctcCart,
        selectedAddress,
        handleIrctcActionClick,
        nonPnrData,
        handleIrctcEditAddress,
        tooltipDismissed,
        trackDeliveryBannerImpression,
    ]);

    return { renderIrctcAddressChange };
};
